import { useState } from 'react'
import { Box, Button, CloseButton, Drawer, HStack, Portal, Text, VStack } from '@chakra-ui/react'
import { useCanvasserContactDetail, useSetDoNotContact } from '../../../../hooks/useCanvasser'
import { useSync } from '../../../../providers/SyncProvider'
import type { TurfContactDto } from '../../../../types/canvasser'
import { DetailsSection } from './DetailsSection'
import { HistorySection } from './HistorySection'
import { NotesSection } from './NotesSection'
import { TagsSection } from './TagsSection'

/** [Spec §4.7] DNC lives on How did it end?; here it is a secondary action, confirmed, online only. */
function DncAction({ contactId, isOnline }: { contactId: string; isOnline: boolean }) {
  const [asking, setAsking] = useState(false)
  const dnc = useSetDoNotContact(contactId)
  const flag = dnc.mutate
  const flagging = dnc.isPending
  if (!isOnline) return <Text fontSize="sm" color="fg.muted">Offline: mark it on How did it end? instead.</Text>
  if (!asking) {
    return <Button minH="44px" variant="ghost" color="danger.fg" alignSelf="flex-start" onClick={() => setAsking(true)}>Flag as Do Not Contact</Button>
  }
  return (
    <Box p={3} borderWidth="1px" borderColor="border" borderRadius="md" bg="bg.subtle">
      <Text mb={2}>They won't be contacted again by your organization.</Text>
      <HStack gap={2}>
        <Button minH="44px" bg="accent" color="accent.fg" disabled={flagging} onClick={() => { if (flagging) return; flag(undefined, { onSuccess: () => setAsking(false) }) }}>Yes, don't contact</Button>
        <Button minH="44px" variant="outline" borderColor="border" onClick={() => setAsking(false)}>Cancel</Button>
      </HStack>
    </Box>
  )
}

function ServerStatus({ kind, onRetry }: { kind: 'loading' | 'offline' | 'error'; onRetry: () => void }) {
  const text = kind === 'loading' ? 'Loading more details…' : kind === 'offline' ? 'More details need a connection' : "Couldn't load more details."
  return (
    <HStack justify="space-between" p={3} borderRadius="md" bg="bg.subtle" color="fg.muted">
      <Text fontSize="sm">{text}</Text>
      {kind === 'error' && <Button size="sm" minH="44px" variant="outline" borderColor="border" onClick={onRetry}>Try again</Button>}
    </HStack>
  )
}

/**
 * [Spec §4.7, §13 Q3, P5-6] The Info sheet. Mounted only while open ([R17]: it opens on the person in
 * focus). Walk-list fields render at once and offline; server sections render when the detail arrives.
 * Never an endless spinner.
 */
export function InfoSheet({ onClose, people, initialContactId }: {
  onClose: () => void; people: TurfContactDto[]; initialContactId: string | null
}) {
  const { isOnline } = useSync()
  const [id, setId] = useState<string | undefined>(initialContactId ?? people[0]?.contactId)
  const person = people.find(p => p.contactId === id) ?? null
  const detail = useCanvasserContactDetail(id)
  const data = detail.data ?? null
  const status: 'ready' | 'loading' | 'offline' | 'error' = data ? 'ready'
    : !isOnline || detail.fetchStatus === 'paused' ? 'offline'
    : detail.isError ? 'error' : 'loading'

  return (
    <Drawer.Root open placement="bottom" onOpenChange={(e) => { if (!e.open) onClose() }}>
      <Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content borderTopRadius="xl" maxH="90vh" pb="env(safe-area-inset-bottom, 0px)" shadow="popover">
            <Drawer.CloseTrigger asChild><CloseButton size="sm" minW="44px" minH="44px" /></Drawer.CloseTrigger>
            <Drawer.Header><Drawer.Title>Info</Drawer.Title></Drawer.Header>
            <Drawer.Body overflowY="auto">
              {people.length > 1 && (
                <HStack gap={2} wrap="wrap" mb={3}>
                  {people.map(p => (
                    <Button key={p.contactId} size="sm" minH="44px" variant="outline" aria-pressed={p.contactId === id}
                      borderColor={p.contactId === id ? 'accent' : 'border'} onClick={() => setId(p.contactId)}>
                      {p.firstName}
                    </Button>
                  ))}
                </HStack>
              )}
              {person && (
                <VStack key={person.contactId} align="stretch" gap={5}>
                  <Box as="section" aria-label="Details">
                    <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" color="fg.muted" mb={2}>Details</Text>
                    <DetailsSection person={person} detail={data} />
                  </Box>
                  {status !== 'ready' && <ServerStatus kind={status} onRetry={() => void detail.refetch()} />}
                  {status === 'ready' && data && (
                    <>
                      <Box as="section" aria-label="History">
                        <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" color="fg.muted" mb={2}>History</Text>
                        <HistorySection detail={data} />
                      </Box>
                      <Box as="section" aria-label="Notes">
                        <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" color="fg.muted" mb={2}>Notes</Text>
                        <NotesSection contactId={person.contactId} detail={data} isOnline={isOnline} />
                      </Box>
                      <Box as="section" aria-label="Tags">
                        <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" color="fg.muted" mb={2}>Tags</Text>
                        <TagsSection contactId={person.contactId} detail={data} isOnline={isOnline} />
                      </Box>
                    </>
                  )}
                  {!(person.isDoNotContact || data?.isDoNotContact) && <DncAction contactId={person.contactId} isOnline={isOnline} />}
                </VStack>
              )}
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  )
}
